import React from "react";
import { MaterialReactTable } from "material-react-table";
import { useTheme, alpha } from "@mui/material/styles";
import { sharedTableProps } from "../shared/tableProps";
import "../util/tables.css";
import { useDataView } from "../../contexts/data.view.context";
import ShowLoadingBar from "../util/showLoadingBar";
import { formatData } from "../util/formatData";
import {
  unifiedEventSummaryColumns,
  getColumnBorderSx,
  getHeaderGroupBg,
} from "./unifiedEventSummaryColumns";
import { useTableAutoRefresh } from "../../hooks/useTableAutoRefresh";
import { getGroupAccent, ToolbarStat, ToolbarLegend, ToolbarRow } from "../shared/monitoringStyles";

/** Normalise all display fields on a raw API row */
function prepareRow(summaryEvent) {
  const fmt = (k) => formatData(summaryEvent[k]);

  const EDSLastDateReceived = fmt("EDSLastDateReceived");
  const EDSDaysNoDataReceived = fmt("EDSDaysNoDataReceived");
  const isBothDash = EDSLastDateReceived === "-" && EDSDaysNoDataReceived === "-";

  const dash = (v) => (isBothDash ? "-" : fmt(v));

  return {
    Source: summaryEvent.Source,
    Entity: summaryEvent.Entity,
    SystemDataSteward: summaryEvent.SystemDataSteward,
    EmailDistributionList: summaryEvent.EmailDistributionList,
    EDSLastDateReceived,
    EDSDaysNoDataReceived,
    EDSCurrentInqueueEvents: isBothDash ? "-" : fmt("EDSCurrentInqueueEvents"),
    EDSQueueName: summaryEvent.EDSQueueName,
    EDSCurrentQueueFlg: summaryEvent.EDSCurrentQueueFlg,
    EDSQueueEventDay: summaryEvent.EDSQueueEventDay,
    EDSEventCountToday: fmt("EDSEventCountToday"),
    EDSEventCountYesterday: fmt("EDSEventCountYesterday"),
    EDSEventCountLast7Days: fmt("EDSEventCountLast7Days"),
    EDSEventCountLast30Days: fmt("EDSEventCountLast30Days"),
    IntegrationErrorCountToday: dash("IntegrationErrorCountToday"),
    IntegrationErrorCountYesterday: dash("IntegrationErrorCountYesterday"),
    IntegrationErrorCountLast7Days: dash("IntegrationErrorCountLast7Days"),
    IntegrationErrorCountLast30Days: dash("IntegrationErrorCountLast30Days"),
    EDSKickoutCountToday: dash("EDSKickoutCountToday"),
    EDSKickoutCountYesterday: dash("EDSKickoutCountYesterday"),
    EDSKickoutCountLast7Days: dash("EDSKickoutCountLast7Days"),
    EDSKickoutCountLast30Days: dash("EDSKickoutCountLast30Days"),
  };
}

function getQueueColor(theme, value) {
  if (value > 1000) return theme.palette.error.main;
  if (value > 500) return theme.palette.warning.main;
  return theme.palette.success.main;
}

function getRowTint(theme, flag) {
  const map = {
    A: alpha(theme.palette.success.main, 0.12),
    B: alpha(theme.palette.warning.main, 0.14),
    C: alpha(theme.palette.secondary.main, 0.12),
  };
  return map[flag];
}

const UnifiedEventSummary = () => {
  const theme = useTheme();
  const { fetchUnifiedEventSummary, unifiedEventSummary } = useDataView();
  const tableMaxHeight = useTableAutoRefresh(fetchUnifiedEventSummary, 300_000);

  if (unifiedEventSummary.length === 0) {
    return <ShowLoadingBar title="Unified Monitoring Summary" loading />;
  }

  // Derived summary values for toolbar
  const totalCurrentInqueueEvents = unifiedEventSummary.reduce(
    (sum, e) => sum + (Number(e.EDSCurrentInqueueEvents) || 0),
    0,
  );

  const firstEntry = unifiedEventSummary[0];
  const lastRefreshedTime = firstEntry?.InsertDate
    ? new Date(firstEntry.InsertDate).toLocaleString("en-US", {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeZoneName: "short",
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "N/A";

  const currentProcessingRecord = unifiedEventSummary.find(
    (e) => Number(e.EDSCurrentQueueFlg) === 1,
  );
  const currentProcessingLabel = currentProcessingRecord
    ? `${currentProcessingRecord.Source} / ${currentProcessingRecord.Entity}`
    : null;

  const tableData = unifiedEventSummary
    .filter((e) => formatData(e.EDSLastDateReceived) !== "-")
    .map(prepareRow);

  return (
    <div className="relative">
      <MaterialReactTable
        {...sharedTableProps}
        columns={unifiedEventSummaryColumns}
        data={tableData}
        enablePinning
        initialState={{
          density: "compact",
          pagination: { pageSize: 100 },
          sorting: [{ id: "EDSDaysNoDataReceived", desc: true }],
          columnPinning: { left: ["Source", "Entity"] },
        }}
        autoResetPageIndex={false}
        renderTopToolbarCustomActions={() => (
          <ToolbarRow>
            <ToolbarStat label="Last Refreshed" value={lastRefreshedTime} color="primary.main" />
            <ToolbarStat
              label="Current Events in Queue"
              value={totalCurrentInqueueEvents}
              color={getQueueColor(theme, totalCurrentInqueueEvents)}
            />
            {currentProcessingLabel && (
              <ToolbarStat
                label="Currently Processing"
                value={currentProcessingLabel}
                color="success.main"
              />
            )}
            <ToolbarLegend />
          </ToolbarRow>
        )}
        muiTopToolbarProps={{ sx: { mt: 3 } }}
        muiTableBodyCellProps={({ cell, column }) => ({
          sx: {
            borderBottom: "1px solid",
            borderColor: "divider",
            textAlign: cell.getValue() === "-" ? "center" : "left",
            padding: "6px 10px",
            fontSize: "0.8rem",
            // Source/Entity are pinned to a fixed width — wrap long values
            // onto multiple lines instead of clipping them.
            ...((column.id === "Source" || column.id === "Entity") && {
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.3,
            }),
            ...getColumnBorderSx(column.id, theme),
          },
        })}
        muiTableHeadCellProps={({ column }) => ({
          sx: {
            backgroundColor: getHeaderGroupBg(column.header, theme, getGroupAccent) || "#F8FAFC",
            color: "text.secondary",
            fontWeight: 700,
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            textAlign: "center",
            borderBottom: "2px solid",
            borderColor: "divider",
            "& .Mui-TableHeadCell-Content": { justifyContent: "center" },
            // MaterialReactTable forces white-space:nowrap on any header
            // label under 20 characters, truncating it with an ellipsis no
            // matter how much column width is available. Several of our
            // headers ("EDS Queue Event Day", "Processing Queue") fall
            // just under that threshold, so force wrapping back on.
            "& .Mui-TableHeadCell-Content-Wrapper": {
              whiteSpace: "normal",
              overflow: "visible",
            },
            ...getColumnBorderSx(column.id, theme),
          },
        })}
        muiTableContainerProps={{ sx: { maxHeight: tableMaxHeight } }}
        muiTableHeadProps={{
          sx: { position: "sticky", top: 0, zIndex: 2, backgroundColor: "background.paper" },
        }}
        muiTableBodyRowProps={({ row }) => {
          const rowTint = getRowTint(theme, row.original.EDSCurrentQueueFlg);
          return {
            sx: {
              "& td": {
                backgroundColor: rowTint || "inherit",
              },
              ...(!rowTint && {
                "&:nth-of-type(even) td": { backgroundColor: "#FAFBFC" },
              }),
              "&:hover td": { backgroundColor: rowTint ? rowTint : "action.hover" },
            },
          };
        }}
      />
    </div>
  );
};

export default UnifiedEventSummary;
